// TKNE · PROJECT PATH — the project the Step belongs to (else the current
// sector's first project with open stages): current stage n of N, next
// milestone, and any earlier stage still undone. Reads c.projects
// (useOracleProjects → designs.basic_stages, the ORDER "Directions" steps; status
// P pending / S active / C completed). No queries of its own, and no writes —
// "Advance stage" opens TKNE, where the real stage write lives.
import type { Sector } from '../../../../domain/olmps';
import type { OracleProject } from '../../../../olmps/useOracleProjects';
import { trunc } from '../../time';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction } from './util';

const BASE = { id: 'tkne', toolId: 'tkne', orderFunction: 'direct', title: 'PROJECT PATH', deityName: 'TKNE', agentId: 'tkne' } as const;

const hasOpen = (p: OracleProject) => (p.steps ?? []).some((s) => s.status !== 'C');

function pickProject(c: WidgetCtx): OracleProject | null {
  const bySector = (c.projects ?? {}) as Partial<Record<Sector, OracleProject[]>>;
  const all = Object.values(bySector).flat() as OracleProject[];
  const designId = c.step?.designId;
  const ofStep = designId ? all.find((p) => p.id === designId && (p.steps ?? []).length > 0) : undefined;
  if (ofStep) return ofStep;
  const pool = c.sector ? bySector[c.sector] ?? [] : all;
  return pool.find(hasOpen) ?? null;
}

export function useTkneWidget(c: WidgetCtx): OrbWidgetModel {
  try {
    const p = pickProject(c);
    if (!p) return emptyModel(BASE, c, 'No active project path');
    const steps = p.steps;
    const total = steps.length;

    // Current = the Active step; if none is marked active, the first one not yet complete.
    let cur = steps.findIndex((s) => s.status === 'S');
    if (cur < 0) cur = steps.findIndex((s) => s.status !== 'C');
    const done = steps.filter((s) => s.status === 'C').length;
    const related = !!c.step?.designId && c.step.designId === p.id;

    const finished = cur < 0;
    const current = finished ? null : steps[cur];
    const next = finished ? null : steps.slice(cur + 1).find((s) => s.status !== 'C') ?? null;
    // A stage before the current one that is still undone blocks the path.
    const blockedIdx = finished ? -1 : steps.findIndex((s, i) => i < cur && s.status !== 'C');
    const blocked = blockedIdx >= 0 ? steps[blockedIdx] : null;

    const lines = [`Project: ${trunc(p.name, 32)}`];
    if (current) lines.push(`Stage ${cur + 1} of ${total}: ${trunc(current.text, 30)}`);
    else lines.push(`All ${total} stages complete`);
    if (next) lines.push(`Next milestone: ${trunc(next.text, 28)}`);
    if (blocked) lines.push(`Blocked by stage ${blockedIdx + 1}: ${trunc(blocked.text, 24)}`);

    const sfx = finished ? ' · Complete' : ` · Stage ${cur + 1}`;
    return {
      ...BASE,
      status: blocked ? 'attention' : related && !finished ? 'active' : 'quiet',
      summary: `${trunc(p.name, 40 - sfx.length)}${sfx}`,
      metrics: [
        { label: 'Stage', value: finished ? 'Done' : `${cur + 1}/${total}` },
        { label: 'Complete', value: `${done}/${total}`, tone: finished ? 'positive' : 'neutral' },
      ],
      alert: blocked ? `Stage ${blockedIdx + 1} not done: ${trunc(blocked.text, 28)}` : null,
      lines,
      primaryAction: { label: 'Advance stage', run: () => c.openTool('tkne'), tone: 'gold' },
      secondaryActions: [openAction(c, 'tkne', 'Tkne')],
      signals: { active: !finished, alerted: !!blocked, related },
    };
  } catch (e) {
    console.warn('[orb widget] tkne model failed', e);
    return emptyModel(BASE, c, 'No active project path');
  }
}
